/**
 * ด่านชั้นที่สองของรูปประกอบโจทย์ (D23) — ฝั่งหน้าจอ
 *
 * ★ ทำไมยังต้องมีทั้งที่ตัวนำเข้าตรวจแล้ว — ด่านที่ตัวนำเข้าป้องกัน "ไฟล์เนื้อหา"
 *   แต่สตริงที่วิ่งมาถึงตรงนี้คือ **ข้อมูลจาก API** ถ้าวันหนึ่งมีทางอื่นที่เขียน
 *   `questions.figure` ได้ (นำเข้าด้วยมือ · ฐานถูกแก้ · endpoint ใหม่) หน้าจอต้อง
 *   ไม่ใช่จุดที่พัง หลักการคือ "ชั้นเดียวไม่พอสำหรับของที่ไหลเข้า DOM"
 *
 * ★ วิธีที่เลือก: **ประกอบ element ขึ้นใหม่ทีละตัว** ด้วย createElementNS
 *   ไม่ใช่ innerHTML แล้วค่อยไล่ลบของแปลก — ต่อให้ allowlist ตรงนี้พลาดสักช่อง
 *   สคริปต์ก็ไม่เคยถูกแนบเข้า document เลยแม้ชั่วขณะ (innerHTML รัน handler ทันที)
 *   DOMParser ที่ใช้ parse ไม่ผูกกับ document จริง จึงไม่มีอะไรทำงานระหว่างแปล
 *
 * รายการอนุญาตต้องตรงกับ `backend/app/services/figure_guard.py` —
 * ตรึงสองฝั่งด้วย `test_allowlist_pinned` (backend) และ `figure.test.ts` (ฝั่งนี้)
 */
const SVG_NS = "http://www.w3.org/2000/svg";

export const ALLOWED_TAGS: ReadonlySet<string> = new Set([
  "svg", "g", "path", "line", "polyline", "polygon", "circle", "ellipse",
  "rect", "text", "tspan", "defs", "marker", "title", "desc",
]); // prettier-ignore

export const ALLOWED_ATTRS: ReadonlySet<string> = new Set([
  "d", "x", "y", "x1", "y1", "x2", "y2", "cx", "cy", "r", "rx", "ry",
  "width", "height", "points", "viewBox", "fill", "stroke", "stroke-width",
  "stroke-dasharray", "stroke-linecap", "transform", "text-anchor",
  "font-size", "dominant-baseline", "class", "marker-end", "id",
  "markerWidth", "markerHeight", "refX", "refY", "orient", "markerUnits",
]); // prettier-ignore

export const MAX_BYTES = 20 * 1024;

const JS_URL = /javascript\s*:/i;
/** url(...) รับเฉพาะที่ชี้กลับเข้าเอกสารตัวเอง เช่น url(#arrow) */
const EXTERNAL_URL = /url\(\s*(?!#)/i;
const DOCTYPE = /<!\s*(DOCTYPE|ENTITY)/i;

function bytes(text: string): number {
  return new TextEncoder().encode(text).length;
}

/** คัดลอก element หนึ่งตัวตาม allowlist — คืน null ถ้าตัวไหนไม่ผ่าน (ทั้งรูปถือว่าไม่ผ่าน) */
function clone(src: Element, doc: Document): SVGElement | null {
  if (src.namespaceURI !== SVG_NS) return null;
  const tag = src.localName;
  if (!ALLOWED_TAGS.has(tag)) return null;
  const out = doc.createElementNS(SVG_NS, tag) as SVGElement;
  for (const attr of Array.from(src.attributes)) {
    // ★ `xmlns` / `xmlns:xlink` เป็น **การประกาศ namespace** ไม่ใช่ข้อมูลของรูป —
    //   DOM ให้มาเป็นแอตทริบิวต์ (ต่างจาก ElementTree ฝั่ง Python ที่ซ่อนให้)
    //   ข้ามไปเฉย ๆ เพราะ createElementNS ใส่ namespace ให้อยู่แล้ว · การประกาศ
    //   xlink ไม่เป็นอันตรายในตัวเอง ตัว `xlink:href` จริงยังตกด่านบรรทัดถัดไป
    if (attr.namespaceURI === "http://www.w3.org/2000/xmlns/") continue;
    // แอตทริบิวต์ที่มี namespace อื่น (xlink:href และพวกเดียวกัน) ตกทั้งหมดตรงนี้
    if (attr.namespaceURI !== null) return null;
    const name = attr.name;
    if (name.toLowerCase().startsWith("on")) return null;
    if (!ALLOWED_ATTRS.has(name)) return null;
    if (JS_URL.test(attr.value) || EXTERNAL_URL.test(attr.value)) return null;
    out.setAttribute(name, attr.value);
  }
  for (const child of Array.from(src.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) {
      out.appendChild(doc.createTextNode(child.nodeValue ?? ""));
      continue;
    }
    if (child.nodeType !== Node.ELEMENT_NODE) continue; // คอมเมนต์/PI ทิ้ง
    const kid = clone(child as Element, doc);
    if (kid === null) return null;
    out.appendChild(kid);
  }
  return out;
}

/**
 * แปลงสตริง SVG เป็น element ที่แนบเข้า DOM ได้ · ไม่ผ่าน = `null` (หน้าจอไม่วาดอะไรเลย)
 *
 * ไม่ผ่านแล้วไม่วาด ดีกว่าวาดบางส่วน — รูปเรขาคณิตที่ขาดเส้นไปเส้นหนึ่ง
 * ทำให้เด็กตอบผิดโดยไม่รู้ตัว ซึ่งแย่กว่าไม่เห็นรูปแล้วรู้ว่ามีอะไรผิด
 */
export function sanitizeFigure(
  svg: string | null | undefined,
): SVGElement | null {
  if (typeof svg !== "string") return null;
  const text = svg.trim();
  if (!text || bytes(text) > MAX_BYTES) return null;
  if (DOCTYPE.test(text)) return null;
  const parsed = new DOMParser().parseFromString(text, "image/svg+xml");
  if (parsed.getElementsByTagName("parsererror").length > 0) return null;
  const root = parsed.documentElement;
  if (root.namespaceURI !== SVG_NS || root.localName !== "svg") return null;
  const out = clone(root, document);
  if (out === null) return null;
  // ป้ายกำกับสำหรับ screen reader มาจาก <title> ของรูปเอง (ถ้าคนวาดใส่มา)
  out.setAttribute("role", "img");
  out.setAttribute("focusable", "false");
  return out;
}
